// SPA → BFF origin wiring (fn-45.4 — cross-origin auth URLs).
//
// **Why this exists:** in the DEPLOYED topology the SPA and the BFF are on
// SEPARATE origins (e.g. `https://devai-console…` vs
// `https://devai-consoleapi…`). The `__Host-session` / `__Host-refresh`
// cookies are host-scoped to the BFF origin, so a login / logout / tRPC
// request MUST target the BFF origin absolutely — a relative `/auth/logout`
// would hit the SPA origin, where the BFF cookies don't exist, and the
// logout would silently fail to clear / revoke the refresh family.
//
// **Why a relative default in dev:** local dev runs the SPA + BFF
// same-origin behind the Vite proxy (see `vite.config` `VITE_BFF_DEV_TARGET`
// proxying `/auth` + `/trpc`). There, `VITE_BFF_URL` is unset and we keep the
// relative paths so the proxy can do its job (and `__Host-` cookies set over
// the proxied HTTPS dev origin land same-origin).
//
// **Cross-origin credentials:** every cross-origin request that must carry
// the `__Host-` cookies sets `credentials: 'include'` at its call site
// (`buildTrpcLinks`, `performLogout`); login is a top-level browser
// navigation so the browser attaches cookies for the BFF origin itself.
// The BFF CORS allow-list (fn-45.4) must echo the SPA origin +
// `Access-Control-Allow-Credentials: true` for those to flow.

/**
 * Read `VITE_BFF_URL` from `import.meta.env`. Returns the BFF origin (no
 * trailing slash) when set, else `undefined` (dev same-origin / proxy).
 *
 * Mirrors `readIngestUrlFromEnv` — the `import.meta.env` access is
 * Vite-specific, so we narrow defensively for node + test environments
 * where `import.meta.env` is absent.
 */
export function readBffOriginFromEnv(): string | undefined {
  const meta = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
  if (meta === undefined) return undefined;
  const raw = meta.VITE_BFF_URL;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  // Strip a trailing slash so `${origin}${path}` never double-slashes.
  return raw.replace(/\/+$/, '');
}

/**
 * Join a BFF origin with an absolute-from-root path. When `origin` is
 * `undefined` (dev same-origin), returns the relative path unchanged so the
 * Vite proxy handles it. `path` MUST start with `/`.
 */
export function bffUrl(path: `/${string}`, origin = readBffOriginFromEnv()): string {
  return origin === undefined ? path : `${origin}${path}`;
}

/**
 * The three BFF endpoints the SPA targets. Absolute against `VITE_BFF_URL`
 * in deployed (cross-origin) mode; relative in dev (Vite proxy). Computed
 * once at module-eval so the mount sites (`main.tsx`, `AppRoutes`) share a
 * single source of truth.
 */
export interface BffEndpoints {
  /** `${VITE_BFF_URL}/auth/login` (or `/auth/login` in dev). */
  readonly loginUrl: string;
  /** `${VITE_BFF_URL}/auth/logout` (or `/auth/logout` in dev). */
  readonly logoutUrl: string;
  /** `${VITE_BFF_URL}/trpc` (or `/trpc` in dev). */
  readonly trpcUrl: string;
  /**
   * `${VITE_BFF_URL}/api/multipart/files` (or relative in dev). The file
   * upload is a credentialed cross-origin POST that must hit the BFF origin
   * — where the `__Host-` cookies + the CSRF/session proxy route live. A
   * relative path would post to the static SPA origin in deployed mode and
   * the upload would fail (no BFF, no cookies, no CSRF preHandler).
   */
  readonly uploadUrl: string;
  /**
   * `${VITE_BFF_URL}/sse/sessions` (or relative `/sse/sessions` in dev) —
   * the SSE stream base path. EventSource connections carry the `__Host-`
   * cookies, so a deployed run-detail stream must open against the BFF
   * origin, not the static SPA origin. Threaded into `useSessionStream` via
   * `buildSessionStreamUrl(sessionId, sseBaseUrl)`.
   */
  readonly sseBaseUrl: string;
}

/**
 * Build the BFF endpoint set. `origin` is injectable for tests; production
 * reads it from `VITE_BFF_URL` once.
 */
export function bffEndpoints(origin = readBffOriginFromEnv()): BffEndpoints {
  return {
    loginUrl: bffUrl('/auth/login', origin),
    logoutUrl: bffUrl('/auth/logout', origin),
    trpcUrl: bffUrl('/trpc', origin),
    uploadUrl: bffUrl('/api/multipart/files', origin),
    sseBaseUrl: bffUrl('/sse/sessions', origin),
  };
}
